import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';

const AdminAccountingPage = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  
  // Helper function to get active branches (same as AdminMemberManagementPage)
  const getActiveBranches = (member) => {
    // ADMIN can see all branches
    if (member?.role === 'ADMIN') {
      return ['NCCU', 'NTU', 'TAIPEI'];
    }
    
    const set = new Set();
    const str = (member?.membership || member?.branch || '').trim();
    if (str) set.add(str);
    if (Array.isArray(member?.memberships)) {
      for (const um of member.memberships) {
        const b = (um?.branchName || '').trim();
        const status = (um?.status || '').toUpperCase();
        if (b && (status === 'ACTIVE' || status === '')) set.add(b);
      }
    }
    return Array.from(set);
  };
  
  // Get available branches based on user role
  const availableBranches = getActiveBranches(user);
  const userBranch = availableBranches.length > 0 ? availableBranches[0] : null;
  const [branchFilter, setBranchFilter] = useState(null); // Start with no branch selected
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  
  // Semesters state
  const [semesters, setSemesters] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSemesterName, setNewSemesterName] = useState('');
  
  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load semesters (only when branchFilter is selected)
  useEffect(() => {
    if (branchFilter) {
      loadSemesters();
    } else {
      setSemesters([]);
    }
  }, [branchFilter]);
  
  const loadSemesters = () => {
    if (!branchFilter) return;
    
    const storedSemesters = localStorage.getItem(`semesters_${branchFilter}`);
    if (storedSemesters) {
      setSemesters(JSON.parse(storedSemesters));
    } else {
      // Initialize with current semester
      const currentSemester = getCurrentSemesterName();
      const initialSemesters = [{
        id: Date.now(),
        name: currentSemester,
        branch: branchFilter,
        createdAt: new Date().toISOString()
      }];
      setSemesters(initialSemesters);
      localStorage.setItem(`semesters_${branchFilter}`, JSON.stringify(initialSemesters));
    }
  };
  
  const getCurrentSemesterName = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    if (month >= 9) {
      return `Fall ${year}`;
    } else if (month >= 2) {
      return `Spring ${year}`;
    } else {
      return `Fall ${year - 1}`;
    }
  };
  
  // Calculate semester stats
  const semesterStats = useMemo(() => {
    return semesters.map(semester => {
      const storedTx = localStorage.getItem(`transactions_${semester.id}`);
      const txList = storedTx ? JSON.parse(storedTx) : [];
      const revenue = txList.filter(t => t.type === 'revenue').reduce((sum, t) => sum + Number(t.amount || 0), 0);
      const expenses = txList.filter(t => t.type === 'expense').reduce((sum, t) => sum + Number(t.amount || 0), 0);
      return {
        ...semester,
        revenue,
        expenses,
        netProfit: revenue - expenses,
        transactionCount: txList.length
      };
    });
  }, [semesters]);
  
  // Create new semester
  const handleCreateSemester = () => {
    if (!branchFilter) {
      alert('Please select a branch first');
      setShowCreateModal(false);
      return;
    }
    
    if (!newSemesterName.trim()) {
      alert('Please enter a semester name');
      return;
    }
    
    const newSemester = {
      id: Date.now(),
      name: newSemesterName.trim(),
      branch: branchFilter,
      createdAt: new Date().toISOString()
    };
    
    const updatedSemesters = [...semesters, newSemester];
    setSemesters(updatedSemesters);
    localStorage.setItem(`semesters_${branchFilter}`, JSON.stringify(updatedSemesters));
    
    setNewSemesterName('');
    setShowCreateModal(false);
    alert(`Semester "${newSemester.name}" created successfully!`);
  };
  
  // Delete semester
  const handleDeleteSemester = (semesterId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this semester and all its transactions?')) return;
    
    const updatedSemesters = semesters.filter(s => s.id !== semesterId);
    setSemesters(updatedSemesters);
    localStorage.setItem(`semesters_${branchFilter}`, JSON.stringify(updatedSemesters));
    localStorage.removeItem(`transactions_${semesterId}`);
  };
  
  // Navigate to semester detail
  const handleViewDetails = (semester) => {
    navigate(`/admin/accounting/${semester.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Create Semester Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-2xl max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Create New Semester</h2>
              <input
                type="text"
                value={newSemesterName}
                onChange={(e) => setNewSemesterName(e.target.value)}
                placeholder="e.g., 2024-FALL, 2025-SPRING"
                className="w-full p-3 border rounded-lg mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreateSemester}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewSemesterName('');
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2"> Finance Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Track revenue, expenses, and manage semester-based accounting for each branch</p>
        </div>

        {/* Branch Filter */}
        {availableBranches.length > 0 ? (
          <div className={`mb-6 ${isMobile ? '' : 'overflow-x-auto'}`}>
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border-2 border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Filter by Branch:</span>
              </div>
              <div className={`flex gap-3 ${isMobile ? 'flex-wrap' : 'min-w-max'}`}>
                {availableBranches.map(branch => (
                  <button
                    key={branch}
                    onClick={() => setBranchFilter(branch)}
                    className={
                      (branchFilter === branch
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg scale-105 border-2 border-blue-500 '
                        : 'bg-white text-gray-700 hover:bg-gray-50 hover:shadow-md border-2 border-gray-300 hover:border-blue-300 ')
                      + `px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${isMobile ? 'flex-1 min-w-0' : 'whitespace-nowrap'}`
                    }
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="font-semibold">{branch}</span>
                      {branchFilter === branch && <span className="text-xs">✓</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              ⚠️ No branches available. Please contact an administrator.
            </p>
          </div>
        )}

        {/* Branch Selection Prompt (when no branch selected) */}
        {!branchFilter && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Select a Branch</h3>
            <p className="text-gray-500">Please select a branch above to view semesters</p>
          </div>
        )}

        {/* Semesters List (Event Management Style) - Only show when branch is selected */}
        {branchFilter && (
          <div className="bg-white rounded-lg shadow-md">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 sm:p-6 border-b">
              <h2 className="text-xl sm:text-2xl font-bold">Semesters - {branchFilter}</h2>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm sm:text-base w-full sm:w-auto"
              >
                + Create Semester
              </button>
            </div>

            {semesterStats.length === 0 ? (
              <div className="p-6 sm:p-12 text-center">
                <div className="text-4xl sm:text-6xl mb-4">📚</div>
                <p className="text-gray-500 text-sm sm:text-base">No semesters found. Click "+ Create Semester" to create one.</p>
              </div>
            ) : isMobile ? (
              /* Mobile Card View */
              <div className="p-4 space-y-4">
                {semesterStats.map(semester => (
                  <div 
                    key={semester.id} 
                    className="bg-gray-50 p-4 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleViewDetails(semester)}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{semester.name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{semester.branch}</p>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Revenue</p>
                            <p className="text-sm font-semibold text-green-600">{semester.revenue.toLocaleString()} NTD</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Expenses</p>
                            <p className="text-sm font-semibold text-red-600">{semester.expenses.toLocaleString()} NTD</p>
                          </div>
                        </div>
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1">Net Profit</p>
                          <p className={`text-lg font-bold ${semester.netProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                            {semester.netProfit.toLocaleString()} NTD
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">{semester.transactionCount} transactions</p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-3 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(semester);
                        }}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium"
                      >
                        View Details
                      </button>
                      <button
                        onClick={(e) => handleDeleteSemester(semester.id, e)}
                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Desktop Table View */
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">SEMESTER</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">BRANCH</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">REVENUE</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">EXPENSES</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">NET PROFIT</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {semesterStats.map(semester => (
                      <tr 
                        key={semester.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewDetails(semester)}
                      >
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{semester.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{semester.branch}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-green-600">{semester.revenue.toLocaleString()} NTD</td>
                        <td className="px-4 py-3 text-sm font-semibold text-red-600">{semester.expenses.toLocaleString()} NTD</td>
                        <td className="px-4 py-3 text-sm font-bold">
                          <span className={semester.netProfit >= 0 ? 'text-blue-600' : 'text-red-600'}>
                            {semester.netProfit.toLocaleString()} NTD
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(semester);
                              }}
                              className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                            >
                              View Details
                            </button>
                            <button
                              onClick={(e) => handleDeleteSemester(semester.id, e)}
                              className="text-red-600 hover:text-red-900 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAccountingPage;
